/**
 * Tool Node Component - Purple colored node for Tools
 * Uses exported Figma assets for icons
 */

import React, { useCallback, useRef, useState } from 'react';
import { Handle, Position, NodeProps } from '@xyflow/react';
import toolIcon from '../../assets/tool.png';
import apiToolIcon from '../../assets/api_integration_tool.png';
import './graphNodes.css';
import NodePopover from './NodePopover';
import { createPortal } from 'react-dom';
import { getNextZIndex } from './globalZIndex';


interface ToolNodeData {
  label: string;
  description?: string;
  isCustom?: boolean;
  vulnerabilityCount?: number;
  guardrails?: string[];
  vulnerabilities?: [];
}

const ToolNode: React.FC<NodeProps<ToolNodeData>> = ({ data }) => {
  const nodeClass = data.isCustom ? 'workflow-node--custom-tool' : 'workflow-node--tool';
  
  // Use API integration tool icon for custom tools, regular tool icon otherwise
  const iconSrc = data.isCustom ? apiToolIcon : toolIcon;

  const [isHovered, setIsHovered] = useState(false);
  const [isClicked, setIsClicked] = useState(false);
  const [popoverPos, setPopoverPos] = useState({x: 0, y: 0});
  const [zIndex, setZIndex] = useState(9999);
  const nodeRef = useRef<HTMLDivElement>(null);
  const isPopoverOpen = isHovered || isClicked;

  const updatePopoverPos = useCallback(() => {
    if (nodeRef.current){
      const rect = nodeRef.current.getBoundingClientRect();
      setPopoverPos({x: rect.left + rect.width / 2, y: rect.top - 10});
    }
  }, []);

  const handleMouseEnter = useCallback(() => {
    updatePopoverPos();
    setZIndex(getNextZIndex());
    setIsHovered(true);
  }, [updatePopoverPos]);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
  }, []);

  const handleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    updatePopoverPos();
    setZIndex(getNextZIndex());
    setIsClicked(true);
  }, [updatePopoverPos]);

  const handleClose = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsClicked(false);
    setIsHovered(false);
  }, []);

  return (
    <>
      <div 
        className={`workflow-node ${nodeClass}`}
        ref={nodeRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
      >
      {/* LEFT: target="left", source="left-source" */}
        <Handle type="target" position={Position.Left} id="left" className="workflow-node__handle" />
        <Handle type="source" position={Position.Left} id="left-source" className="workflow-node__handle" />

        {/* RIGHT: source="right", target="right-target" */}
        <Handle type="source" position={Position.Right} id="right" className="workflow-node__handle" />
        <Handle type="target" position={Position.Right} id="right-target" className="workflow-node__handle" />

        {/* TOP: target="top", source="top-source" */}
        <Handle type="target" position={Position.Top} id="top" className="workflow-node__handle" />
        <Handle type="source" position={Position.Top} id="top-source" className="workflow-node__handle" />

        {/* BOTTOM: source="bottom", target="bottom-target" */}
        <Handle type="source" position={Position.Bottom} id="bottom" className="workflow-node__handle" />
        <Handle type="target" position={Position.Bottom} id="bottom-target" className="workflow-node__handle" />
          
        <div className="workflow-node__container">
          <img 
            src={iconSrc} 
            alt={data.isCustom ? "Custom Tool" : "Tool"} 
            className="workflow-node__asset-icon"
          />
        </div>
        
        <div className="workflow-node__label">{data.label}</div>
      </div>

      {isPopoverOpen && createPortal(
        <NodePopover
          isOpen={isPopoverOpen}
          position={popoverPos}
          zIndex={zIndex}
          onClose={handleClose}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          label={data.label}
          description={data.description}
          vulnerabilityCount={data.vulnerabilityCount ?? 0}
          guardrails={data.guardrails ?? []}
          vulnerabilities={data.vulnerabilities ?? []}
          onVulnerabilityClick={data.onVulnerabilityClick}
        />,
        document.body
      )}
    </>
  );
};

export default ToolNode;
